import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Building2, Users, MapPin } from "lucide-react";
import { api, formatCompactNumber, formatINR } from "@/lib/api";
import { imageForCity, imageForFormat } from "@/lib/images";
import SmartImage from "@/components/common/SmartImage";

export default function CityDetail() {
  const { city } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/public/cities/${encodeURIComponent(city)}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [city]);

  if (loading) return <div className="pt-40 max-w-[1440px] mx-auto px-6 text-[var(--slate-2)]">Loading…</div>;
  if (!data) return <div className="pt-40 max-w-[1440px] mx-auto px-6 text-[var(--slate-2)]">City not found.</div>;

  const totalFootfall = data.malls.reduce((a, m) => a + (m.monthly_footfall || 0), 0);
  const heroImg = imageForCity(data.city) || data.malls[0]?.hero_image;

  return (
    <div data-testid="city-detail" className="bg-[var(--bone)]">
      <section className="relative h-[60vh] lg:h-[70vh] overflow-hidden">
        <img src={heroImg} alt={data.city} className="absolute inset-0 w-full h-full object-cover img-luxe" />
        <div className="absolute inset-0 hero-overlay-strong" />
        <div className="relative h-full max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 flex flex-col justify-end pb-12 lg:pb-20">
          <Link to="/cities" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-white/85 hover:text-[var(--gold-light)] mb-7">
            <ArrowLeft size={12} /> All cities
          </Link>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--gold-light)] font-semibold">{data.malls[0]?.state || "India"}</div>
          <h1 className="mt-3 font-serif-display text-6xl md:text-7xl lg:text-8xl text-white">{data.city}</h1>
          <div className="mt-6 flex flex-wrap gap-x-10 gap-y-3 text-sm">
            <Stat icon={Building2} value={data.malls.length} label="Premium malls" />
            <Stat icon={Users} value={formatCompactNumber(totalFootfall)} label="Monthly shoppers" />
            <Stat icon={MapPin} value={data.inventory.length} label="Live placements" />
          </div>
        </div>
      </section>

      {/* Malls */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-16 lg:py-20">
        <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[var(--gold)]">Malls</div>
        <h2 className="mt-3 font-serif-display text-4xl lg:text-5xl text-[var(--ink)]">Premium destinations in {data.city}</h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.malls.map((m) => (
            <div key={m.id} className="surface surface-hover" data-testid={`mall-card-${m.id}`}>
              <div className="relative aspect-[16/10] overflow-hidden">
                {m.hero_image && <SmartImage src={m.hero_image} city={data.city} alt={m.name} className="w-full h-full object-cover img-luxe" />}
                <div className="absolute top-3 left-3 chip chip-gold chip-active">{m.category}</div>
              </div>
              <div className="p-6">
                <div className="font-serif-display text-2xl text-[var(--ink)]">{m.name}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--slate-2)] mt-1">{m.tier}</div>
                <p className="mt-3 text-sm text-[var(--slate-1)] line-clamp-2">{m.description}</p>
                <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-[var(--gold)] font-semibold">
                  {m.spot_count || formatCompactNumber(m.monthly_footfall)} {m.spot_count ? "ad spaces" : "monthly shoppers"}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Inventory */}
      <section className="bg-[var(--bone-warm)] py-16 lg:py-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[var(--gold)]">Inventory</div>
              <h2 className="mt-3 font-serif-display text-4xl lg:text-5xl text-[var(--ink)]">{data.inventory.length} live placements</h2>
            </div>
            <Link to="/marketplace" className="btn-secondary">Open in marketplace</Link>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)]">
            {data.inventory.slice(0, 15).map((item) => (
              <Link key={item.id} to={`/inventory/${item.id}`} className="bg-[var(--bone-warm)] p-5 flex items-center justify-between gap-3 hover:bg-[var(--bone)] transition-colors" data-testid={`city-inv-${item.id}`}>
                <div className="min-w-0">
                  <div className="font-serif text-lg text-[var(--ink)] truncate">{item.format}</div>
                  <div className="text-xs text-[var(--slate-2)] mt-1 truncate">{item.mall_name}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[var(--gold)] text-sm font-medium font-mono">{formatINR(item.price_per_day)}</div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--slate-2)]">/ day</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="flex items-center gap-3 text-white">
      <Icon size={16} className="text-[var(--gold-light)]" />
      <span className="font-serif text-2xl">{value}</span>
      <span className="text-[10px] uppercase tracking-[0.22em] text-white/65 ml-1">{label}</span>
    </div>
  );
}
